import { supabase } from '../../lib/supabase';
import { slugify, fetchLatestFortnightBreakdown, formatFortnightRange } from '../market-intelligence/utils';
import { normalizeGrade } from '../live-tender-info/matching';

// Public "mandi" listing — deliberately the inverse of fetchFactoryCards() in
// factory-rate-intelligence-center/data.js: that one starts from mi_mills and
// looks for a directory match; this one starts from live sellable inventory
// (mill_price_quotes.status = 'live') and only ever shows a mill that actually
// has something for sale right now. No phone/email ever selected — public page.

export const DIRECTORY_COLUMNS = 'id, factory_name, plant_name, address, mill_code, trusted_pincode, source_pincode, operational_status';

const isoMonthsAgo = (n) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

// Same LATERAL-join replacement used by factory-rate-intelligence-center/data.js —
// kept identical on purpose so address never drifts between the two pages.
export const matchDirectoryFactory = (mill, directoryRows) => {
  const candidates = (directoryRows || []).filter((f) =>
    f?.mill_code === mill?.mill_code || f?.trusted_pincode === mill?.pincode || f?.source_pincode === mill?.pincode
  );
  if (candidates.length === 0) return null;
  const priority = (f) => (f?.mill_code === mill?.mill_code ? 1 : f?.trusted_pincode === mill?.pincode ? 2 : 3);
  const statusRank = (f) => (f?.operational_status === 'Working' ? 0 : 1);
  return [...candidates].sort((a, b) => {
    const pd = priority(a) - priority(b);
    return pd !== 0 ? pd : statusRank(a) - statusRank(b);
  })[0];
};

export const fetchMandiListings = async () => {
  const { data: liveQuotes, error: lqErr } = await supabase
    .from('mill_price_quotes')
    .select('id, mill_id, grade, rate_excl_gst, rate_incl_gst, payment_date, lifting_date, available_qty, approved_at')
    .eq('status', 'live')
    .order('approved_at', { ascending: false });

  if (lqErr) return { factories: [], error: `mill_price_quotes: ${lqErr.message}` };
  if (!liveQuotes || liveQuotes.length === 0) return { factories: [], error: null };

  const millIds = [...new Set(liveQuotes.map((q) => q.mill_id))];
  const windowStart = isoMonthsAgo(12);

  const [millsRes, directoryRes, roRes, newsRes] = await Promise.all([
    supabase.from('mi_mills').select('id, canonical_name, mill_code, pincode, source_state, source_district').in('id', millIds),
    supabase.from('internal_directory_factories').select(DIRECTORY_COLUMNS),
    supabase.from('mi_release_orders').select('mill_id, period_month, allocated_qty_mt').in('mill_id', millIds).gte('period_month', windowStart).order('period_month', { ascending: true }),
    supabase.from('mi_factory_news').select('mill_id, title, url, published_at').in('mill_id', millIds).order('published_at', { ascending: false, nullsFirst: false }),
  ]);

  if (millsRes.error) return { factories: [], error: `mi_mills: ${millsRes.error.message}` };
  if (directoryRes.error) return { factories: [], error: `internal_directory_factories: ${directoryRes.error.message}` };
  if (roRes.error) return { factories: [], error: `mi_release_orders: ${roRes.error.message}` };
  if (newsRes.error) return { factories: [], error: `mi_factory_news: ${newsRes.error.message}` };

  // Same source Market Intelligence's homepage uses for its separate "Sep 1-15, 2026 (MT)"
  // column — mi_release_orders itself isn't touched until BOTH fortnights of a month are
  // known (see fetchFortnightFYByMill's doc comment in market-intelligence/utils.js), so
  // the "latest complete month" quota above can legitimately lag a fortnight or two behind
  // what's already been publicly notified. Shown as an ADDITIONAL line below, never
  // overwriting quotaMonthLabel/quotaQty — conflating a partial fortnight with a full
  // month's figure would misrepresent the data. Never lets a fortnight-fetch failure take
  // down the whole Mandi listing.
  let latestFortnight = null;
  try {
    latestFortnight = await fetchLatestFortnightBreakdown();
  } catch (_e) {
    latestFortnight = null;
  }

  const gradesByMill = {};
  const seenMillGrade = new Set();
  liveQuotes.forEach((q) => {
    const dedupeKey = `${q.mill_id}::${q.grade}`;
    if (seenMillGrade.has(dedupeKey)) return; // superseded historical snapshot — already have the current one
    seenMillGrade.add(dedupeKey);
    (gradesByMill[q.mill_id] ||= []).push({
      id: q.id,
      grade: q.grade,
      rateExclGst: Number(q.rate_excl_gst),
      rateInclGst: Number(q.rate_incl_gst),
      paymentDate: q.payment_date,
      liftingDate: q.lifting_date,
      availableQty: Number(q.available_qty),
    });
  });

  const roByMill = {};
  (roRes.data || []).forEach((r) => { (roByMill[r.mill_id] ||= []).push(r); });

  const newsByMill = {};
  (newsRes.data || []).forEach((n) => { (newsByMill[n.mill_id] ||= []).push(n); });

  const factories = (millsRes.data || []).map((mill) => {
    const factory = matchDirectoryFactory(mill, directoryRes.data);
    const ro = (roByMill[mill.id] || []).slice().sort((a, b) => (a.period_month < b.period_month ? -1 : 1));
    const latestRo = ro.length > 0 ? ro[ro.length - 1] : null;

    // Mandi shows exactly ONE quota figure — the most RECENT one, not "latest complete
    // calendar month." mi_release_orders isn't touched until both fortnights of a month
    // are known (see fetchFortnightFYByMill's doc comment in market-intelligence/utils.js),
    // so a newer fortnight can already be public — typically notified ~28th of the prior
    // month — before that month's mi_release_orders row exists at all. When that's the
    // case, the fortnight figure REPLACES the stale month figure here; it never sits
    // alongside it. (Market Intelligence's dashboard can afford to show both as separate
    // KPI tiles — Mandi is a single buyer-facing fact, not an analytics page, so two
    // numbers here just reads as conflicting data.) Same self-disabling guard
    // fetchFortnightFYByMill uses: compare calendar months (both truncated to the 1st),
    // not raw dates — once this month's fortnights are eventually combined into a single
    // mi_release_orders row, that row becomes latestRo and this stops firing for this
    // mill automatically, no flag to flip.
    const monthStart = (dateStr) => `${dateStr.slice(0, 7)}-01`;
    const fortnightQty = latestFortnight?.byMill?.get(mill.id);
    const fortnightIsNewer = latestFortnight && fortnightQty != null &&
      (!latestRo || monthStart(latestFortnight.periodStart) > latestRo.period_month);

    const quotaLabel = fortnightIsNewer
      ? (formatFortnightRange(latestFortnight.periodStart, latestFortnight.periodEnd) || latestFortnight.periodLabel)
      : (latestRo ? new Date(latestRo.period_month).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : null);
    const quotaQty = fortnightIsNewer ? Number(fortnightQty) : (latestRo ? Number(latestRo.allocated_qty_mt) : null);

    return {
      id: mill.id,
      millId: mill.id,
      name: factory?.factory_name || mill.canonical_name,
      millCode: mill.mill_code,
      state: mill.source_state || '—',
      district: mill.source_district || '—',
      address: factory?.address || null,
      pincode: mill.pincode || null,
      quotaMonthLabel: quotaLabel,
      quotaQty,
      quotaIsFortnight: fortnightIsNewer,
      past12: ro.slice(-12).map((r) => ({
        month: new Date(r.period_month).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
        qty: Number(r.allocated_qty_mt),
      })),
      news: (newsByMill[mill.id] || []).slice(0, 3).map((n) => ({
        title: n.title,
        url: n.url,
        date: n.published_at ? new Date(n.published_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : null,
      })),
      grades: gradesByMill[mill.id] || [],
    };
  }).filter((f) => f.grades.length > 0);

  return { factories, error: null };
};

// Detail page (/mandi/:factorySlug) — same fetch-all-then-match-by-slug pattern
// MIFactoryPage already uses for /market-intelligence/factory/:millSlug, so the
// two pages can never disagree about what a given slug resolves to.
export const fetchMandiFactoryBySlug = async (slug) => {
  const { factories, error } = await fetchMandiListings();
  if (error) return { factory: null, error };
  const factory = factories.find((f) => slugify(f.name) === slug) || null;
  if (!factory) return { factory: null, error: 'Listing not found — it may have sold out or been removed.' };

  // Summary block needs metrics/rank — only fetched here (single mill), not on
  // the grid, to keep the /mandi listing fetch cheap.
  const [{ data: metricsRow }, { data: rankRow }, { count: totalMills }] = await Promise.all([
    supabase.from('mi_v_mill_metrics').select('latest_qty_mt, max_qty_mt, max_qty_month, latest_month').eq('mill_id', factory.millId).maybeSingle(),
    supabase.from('mi_v_mill_rank_latest').select('national_rank, state_rank').eq('mill_id', factory.millId).maybeSingle(),
    supabase.from('mi_mills').select('id', { count: 'exact', head: true }),
  ]);

  return { factory: { ...factory, metrics: metricsRow || null, ranks: rankRow ? { ...rankRow, totalMills: totalMills || 0 } : null }, error: null };
};

// SugarHathi's OWN live/in-stock offer for one specific mi_mills.id — the ONLY
// source of truth for whether a "Buy Now" section may appear on that factory's
// page. Deliberately keyed by mill_id (the real foreign key linking
// mill_price_quotes -> mi_mills), not by name-matching, so it can never
// misattribute one factory's inventory to another. Returns { offer: null } when
// SugarHathi has no live quota/in-stock quote for this mill — callers must not
// render Buy Now in that case, regardless of whatever tender data exists for it.
export const fetchMandiOfferForMill = async (millId) => {
  if (!millId) return { offer: null, error: null };

  const { data: quotes, error: qErr } = await supabase
    .from('mill_price_quotes')
    .select('id, grade, rate_excl_gst, rate_incl_gst, payment_date, lifting_date, available_qty, approved_at')
    .eq('mill_id', millId)
    .eq('status', 'live')
    .order('approved_at', { ascending: false });
  if (qErr) return { offer: null, error: qErr.message };
  if (!quotes || quotes.length === 0) return { offer: null, error: null };

  const [{ data: mill, error: mErr }, { data: directoryRows, error: dErr }] = await Promise.all([
    supabase.from('mi_mills').select('id, canonical_name').eq('id', millId).maybeSingle(),
    supabase.from('internal_directory_factories').select(DIRECTORY_COLUMNS),
  ]);
  if (mErr) return { offer: null, error: mErr.message };
  if (dErr) return { offer: null, error: dErr.message };

  const factory = matchDirectoryFactory(mill, directoryRows);
  const factoryName = factory?.factory_name || mill?.canonical_name;
  const factorySlug = slugify(factoryName);

  const grades = new Map();
  quotes.forEach((q) => {
    const key = normalizeGrade(q.grade);
    if (grades.has(key)) return; // already set from the newest row for this grade (quotes is pre-sorted approved_at desc) — don't let an older snapshot overwrite it
    grades.set(key, {
      id: q.id,
      grade: q.grade,
      rateExclGst: Number(q.rate_excl_gst),
      rateInclGst: Number(q.rate_incl_gst),
      paymentDate: q.payment_date,
      liftingDate: q.lifting_date,
      availableQty: Number(q.available_qty),
    });
  });

  return { offer: { factorySlug, factoryName, grades }, error: null };
};


